import * as Tooltip from "@radix-ui/react-tooltip";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router";

import { ServiceProblemBanner } from "@/components/ServiceProblemNotice";
import { cn } from "@/lib/cn";
import { DRAWER_QUERY, useMediaQuery } from "@/lib/use-media-query";

import { Header } from "./Header";
import { ShortcutsDialog } from "./ShortcutsDialog";
import { Sidebar } from "./Sidebar";
import { ViewAsBanner } from "./ViewAsBanner";

const COLLAPSED_KEY = "tv.sidebar.collapsed";

function readCollapsed(): boolean {
  try {
    const stored = localStorage.getItem(COLLAPSED_KEY);
    if (stored !== null) return stored === "1";
  } catch {
    // storage unavailable
  }
  // Tablets start with the icon rail to leave room for content.
  return window.matchMedia("(max-width: 1279px)").matches;
}

export function AppShell() {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  // The drawer only exists on small screens: growing the window closes it.
  const smallScreen = useMediaQuery(DRAWER_QUERY);
  const drawerOpen = mobileOpen && smallScreen;

  useEffect(() => {
    if (!drawerOpen) return;
    // Keep the page behind the open drawer still, and let Escape close it.
    const root = document.documentElement;
    const previous = root.style.overflow;
    root.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      root.style.overflow = previous;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [drawerOpen]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((current) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, current ? "0" : "1");
      } catch {
        // not persisted
      }
      return !current;
    });
  }, []);

  return (
    <Tooltip.Provider delayDuration={300}>
      <div className="min-h-dvh">
        <a
          href="#main"
          className="sr-only z-50 rounded-control bg-surface px-3 py-2 shadow-pop focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
        >
          {t("app.skipToContent")}
        </a>
        <Sidebar
          collapsed={collapsed}
          mobileOpen={drawerOpen}
          onToggleCollapsed={toggleCollapsed}
          onCloseMobile={() => setMobileOpen(false)}
        />
        <div
          className={cn(
            "flex min-h-dvh flex-col transition-[padding] duration-200 ease-out",
            collapsed ? "md:pl-rail" : "md:pl-sidebar",
          )}
        >
          <ViewAsBanner />
          <Header menuOpen={drawerOpen} onOpenMenu={() => setMobileOpen(true)} />
          <ServiceProblemBanner />
          <main
            id="main"
            tabIndex={-1}
            className="mx-auto w-full max-w-[90rem] flex-1 px-4 py-6 sm:px-6 lg:px-8"
          >
            <Outlet />
          </main>
          <ShortcutsDialog />
        </div>
      </div>
    </Tooltip.Provider>
  );
}
